import { Modal, createStyles } from "@mantine/core";
import { LuDownload } from "react-icons/lu";

const useStyles = createStyles(() => ({
  rcLetterhead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "16px 18px",
    background: "#FAFAFA",
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    marginBottom: "18px",
  },
  rcLetterheadLeft: { display: "flex", flexDirection: "column", gap: "2px" },
  rcBrand: { fontSize: "16px", fontWeight: 800, color: "#171717", letterSpacing: "-0.012em", fontFamily: "Inter" },
  rcBrandSub: { fontSize: "12px", fontWeight: 600, color: "#262626", fontFamily: "Inter" },
  rcBrandMeta: { fontSize: "11.5px", color: "#667085", marginTop: "4px", fontFamily: "Inter" },
  rcPaidStamp: {
    display: "inline-block",
    fontSize: "14px",
    fontWeight: 800,
    letterSpacing: "0.16em",
    color: "#047857",
    background: "#DCFCE7",
    border: "2px solid #86E1B0",
    borderRadius: "8px",
    padding: "8px 16px",
    transform: "rotate(-6deg)",
    fontFamily: "Inter",
  },
  rcKvGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "10px",
  },
  rcKv: {
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    padding: "10px 12px",
  },
  rcKvKey: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "4px",
    fontFamily: "Inter",
  },
  rcKvVal: { fontSize: "13px", fontWeight: 700, color: "#171717", fontFamily: "Inter" },
  rcSectionLabel: {
    margin: "20px 0 8px",
    fontSize: "11.5px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "Inter",
  },
  rcLines: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    overflow: "hidden",
    fontSize: "13px",
    fontFamily: "Inter",
    "& thead th": {
      background: "#FAFAFA",
      fontSize: "11px",
      fontWeight: 700,
      color: "#667085",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "10px 12px",
      textAlign: "left",
      borderBottom: "1px solid #E5E5E5",
    },
    "& th.r, & td.r": { textAlign: "right" },
    "& tbody td": { padding: "12px", color: "#171717" },
    "& tfoot td": {
      padding: "12px",
      background: "#FAFAFA",
      borderTop: "1px solid #E5E5E5",
      fontWeight: 800,
      color: "#171717",
    },
  },
  rcFootnote: {
    margin: "16px 0 0",
    fontSize: "12px",
    color: "#667085",
    lineHeight: 1.55,
    fontFamily: "Inter",
  },
  foot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    paddingTop: "18px",
    borderTop: "1px solid #F1F1F1",
    marginTop: "4px",
  },
  closeBtn: {
    height: "40px",
    padding: "0 16px",
    borderRadius: "8px",
    border: "1px solid #E5E5E5",
    background: "#fff",
    color: "#262626",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    "&:hover": { background: "#F4F4F5" },
  },
  downloadBtn: {
    height: "40px",
    padding: "0 16px",
    borderRadius: "8px",
    border: 0,
    background: "#FFBA45",
    color: "#171717",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    "& svg": { width: "16px", height: "16px" },
    "&:hover": { background: "#EEAA33" },
  },
}));

const ReceiptViewModal = ({ opened, onClose, receipt }) => {
  const { classes } = useStyles();

  if (!receipt) return null;

  const claimId = receipt.desc?.match(/CLM-\d+/)?.[0] || "—";
  const payer = receipt.desc?.split("paid by ")?.[1] || "—";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={620}
      zIndex={1001}
      padding="xl"
      radius={12}
      title={
        <div>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "Inter" }}>
            Receipt
          </p>
          <h2 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 700, color: "#171717", letterSpacing: "-0.018em", fontFamily: "Inter" }}>
            {receipt.num}
          </h2>
          <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#667085", fontFamily: "Inter" }}>
            {receipt.date}
          </p>
        </div>
      }
      styles={{
        header: { borderBottom: "1px solid #F1F1F1", paddingBottom: 16, marginBottom: 20, alignItems: "flex-start" },
        title: { flex: 1 },
        close: { width: 36, height: 36, borderRadius: 8, background: "#F4F4F5", color: "#262626", marginTop: 4, "&:hover": { background: "#E4E4E7" } },
      }}
    >
      {/* Letterhead */}
      <div className={classes.rcLetterhead}>
        <div className={classes.rcLetterheadLeft}>
          <div className={classes.rcBrand}>Ogiso, Inc.</div>
          <div className={classes.rcBrandSub}>Billing &amp; Claims Operations</div>
          <div className={classes.rcBrandMeta}>support@ogiso.io · 757-600-0297</div>
        </div>
        <div>
          <div className={classes.rcPaidStamp}>PAID</div>
        </div>
      </div>

      {/* KV grid */}
      <div className={classes.rcKvGrid}>
        <div className={classes.rcKv}>
          <div className={classes.rcKvKey}>Receipt number</div>
          <div className={classes.rcKvVal}>{receipt.num}</div>
        </div>
        <div className={classes.rcKv}>
          <div className={classes.rcKvKey}>Issued date</div>
          <div className={classes.rcKvVal}>{receipt.date}</div>
        </div>
        <div className={classes.rcKv}>
          <div className={classes.rcKvKey}>Related claim</div>
          <div className={classes.rcKvVal}>{claimId}</div>
        </div>
        <div className={classes.rcKv}>
          <div className={classes.rcKvKey}>Payer</div>
          <div className={classes.rcKvVal}>{payer}</div>
        </div>
      </div>

      {/* Line items */}
      <h6 className={classes.rcSectionLabel}>Line items</h6>
      <table className={classes.rcLines}>
        <thead>
          <tr>
            <th>Description</th>
            <th className="r">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{receipt.desc}</td>
            <td className="r">{receipt.amount}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>Total deducted from remittance</td>
            <td className="r">{receipt.amount}</td>
          </tr>
        </tfoot>
      </table>

      <p className={classes.rcFootnote}>
        Processing fees are deducted automatically from each remittance. This record is retained for your books and is downloadable as a PDF for accounting reconciliation.
      </p>

      {/* Footer */}
      <div className={classes.foot}>
        <button type="button" className={classes.closeBtn} onClick={onClose}>
          Close
        </button>
        <button type="button" className={classes.downloadBtn}>
          <LuDownload />
          Download PDF
        </button>
      </div>
    </Modal>
  );
};

export default ReceiptViewModal;
